import { useMemo, useState } from 'react'
import { downloadDeployBlob } from '@/api/guardianforge/deploy'
import type { ReleaseManifest } from '@/api/guardianforge/types'
import {
  DeploymentCard,
  DeploymentError,
  DeploymentJsonView,
  DeploymentSpinner,
} from '@/components/deployment/DeploymentUi'
import { useDeployApi } from '@/hooks/useDeployApi'
import { formatKstDateTime } from '@/utils/datetime'
import ModelAssignmentPanel from './ModelAssignmentPanel'

export default function ModelRegistryPage() {
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<ReleaseManifest | null>(null)
  const [assignmentMutationBusy, setAssignmentMutationBusy] = useState(false)
  const releases = useDeployApi<{ releases: ReleaseManifest[] }>(
    '/releases',
    { query: { limit: 200 } },
    null,
  )

  const filtered = useMemo(() => {
    const list = releases.data?.releases ?? []
    if (!filter) return list
    const needle = filter.toLowerCase()
    return list.filter((release) =>
      `${release.model_key} ${release.version} ${release.dataset_id ?? ''}`.toLowerCase().includes(needle),
    )
  }, [releases.data, filter])

  return (
    <>
      <h2>Model Registry</h2>
      <p className="deployment-subtitle">GuardianForge release registry와 review/decision 역참조를 확인합니다.</p>

      <div className="deployment-toolbar">
        <input
          placeholder="filter (model_key / version / dataset)"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          style={{ width: 360 }}
        />
        <button onClick={() => releases.refresh()}>새로고침</button>
        {releases.loading && <DeploymentSpinner />}
      </div>

      <DeploymentError error={releases.error} />

      <div className="deployment-grid cols-2">
        <DeploymentCard title={`Models (${filtered.length})`}>
          <div style={{ maxHeight: 520, overflow: 'auto' }}>
            <table className="deployment-table">
              <thead>
                <tr>
                  <th>model_key</th>
                  <th>version</th>
                  <th>format</th>
                  <th>review</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((release) => {
                  const reviewBadge = release.review?.status ?? '-'
                  const isSelected = selected?.model_key === release.model_key && selected?.version === release.version
                  return (
                    <tr
                      key={`${release.model_key}/${release.version}`}
                      className={isSelected ? 'selected' : assignmentMutationBusy ? '' : 'clickable'}
                      aria-disabled={assignmentMutationBusy}
                      title={assignmentMutationBusy ? '노드 배정 작업이 끝난 뒤 다른 릴리스를 선택할 수 있습니다.' : undefined}
                      onClick={() => {
                        if (!assignmentMutationBusy) setSelected(release)
                      }}
                    >
                      <td className="deployment-mono">{release.model_key}</td>
                      <td className="deployment-mono">{release.version}</td>
                      <td>{release.format}</td>
                      <td>
                        <span className={`deployment-badge ${reviewBadge === 'approved' ? 'ok' : 'muted'}`}>
                          {reviewBadge}
                        </span>
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="deployment-muted">
                      모델 registry 항목이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DeploymentCard>

        {selected ? (
          <DeploymentCard title={`${selected.model_key} / ${selected.version}`}>
            <div className="deployment-grid cols-2">
              <div>
                <label>Format</label>
                <div className="deployment-mono">{selected.format}</div>
              </div>
              <div>
                <label>Created</label>
                <div className="deployment-mono deployment-muted">{formatKstDateTime(selected.created_at)}</div>
              </div>
              <div>
                <label>Dataset</label>
                <div className="deployment-mono">{selected.dataset_id ?? '-'}</div>
              </div>
              <div>
                <label>MLflow Run</label>
                <div className="deployment-mono">{selected.mlflow_run_id ?? '-'}</div>
              </div>
              <div>
                <label>SHA256</label>
                <div className="deployment-mono" style={{ wordBreak: 'break-all' }}>{selected.artifact_sha256}</div>
              </div>
              <div>
                <label>Bytes</label>
                <div className="deployment-mono">{selected.artifact_size ?? '-'}</div>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <ModelAssignmentPanel
                key={`${selected.model_key}\u0000${selected.version}`}
                release={selected}
                onMutationBusyChange={setAssignmentMutationBusy}
              />
              <div className="deployment-flex" style={{ marginTop: 8 }}>
                <ArtifactDownloadButton modelKey={selected.model_key} version={selected.version} />
              </div>
            </div>
            {selected.review && (
              <div style={{ marginTop: 12 }}>
                <label>Review</label>
                <DeploymentJsonView data={selected.review} />
              </div>
            )}
            <details style={{ marginTop: 12 }}>
              <summary className="deployment-muted">Full manifest</summary>
              <DeploymentJsonView data={selected} />
            </details>
          </DeploymentCard>
        ) : (
          <DeploymentCard title="상세">
            <div className="deployment-muted">목록에서 모델 registry 항목을 선택하세요.</div>
          </DeploymentCard>
        )}
      </div>
    </>
  )
}

function ArtifactDownloadButton({ modelKey, version }: { modelKey: string; version: string }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const download = async () => {
    setBusy(true)
    setError(null)
    try {
      await downloadDeployBlob(
        `/releases/${encodeURIComponent(modelKey)}/${encodeURIComponent(version)}/artifact`,
        `${modelKey}-${version}.tar.gz`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <button onClick={download} disabled={busy}>
        {busy ? '다운로드 중...' : 'Artifact 다운로드 (.tar.gz)'}
      </button>
      {error && <div className="deployment-error-box">{error}</div>}
    </div>
  )
}
